import { ChevronLeft } from "@certa/icons/components/ChevronLeft";
import { ChevronRight } from "@certa/icons/components/ChevronRight";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import { Stack } from "@certa/catalyst/layouts/Stack";
import {
  Typography,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { classNames } from "@certa/catalyst/utils/common";
import { useIntl } from "react-intl";
import styles from "./TPagination.module.css";

type TPaginationProps = {
  /**
   * The page size of each page.
   * @default 10
   */
  pageSize?: number;
  /**
   * The total number of items.
   */
  totalCount: number;
  /**
   * The current page number.
   * @default 1
   */
  currentPage: number;
  /**
   * Callback function that is called when the page is changed.
   * @param page The new page number.
   */
  onPageChange: (page: number) => void;
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  // First page should always be visible
  // string, number is used to represent the jump buttons
  const visiblePages: (string | number)[] = [1];

  // If there is only one page, return the visible pages with just the first page
  if (totalPages <= 1) {
    return visiblePages;
  }

  if (totalPages <= 5) {
    for (let i = 2; i <= totalPages; i++) {
      visiblePages.push(i);
    }
    return visiblePages;
  }

  let startPage = Math.max(2, currentPage - 2);
  let endPage = Math.min(totalPages - 1, currentPage + 2);

  // If the current page is close to the last page, show the last 5 pages
  if (startPage > totalPages - 4) {
    startPage = totalPages - 4;
  }

  // If the current page is close to the first page, show the first 5 pages
  if (endPage < 5) {
    endPage = 5;
  }

  // if the start page is not 2, then add the previous jump button
  if (startPage - 1 !== 1) {
    visiblePages.splice(1, 0, "previousJump");
  }

  for (let i = startPage; i <= endPage; i++) {
    visiblePages.push(i);
  }

  // if the end page is not the last page, then add the next jump button
  if ((visiblePages[visiblePages.length - 1] as number) + 1 !== totalPages) {
    visiblePages.push("nextJump");
  }

  // Last page should always be visible
  visiblePages.push(totalPages);

  return visiblePages;
};

export const TPagination = (props: TPaginationProps) => {
  const { currentPage, totalCount, onPageChange, pageSize = 10 } = props;
  const intl = useIntl();

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  // Jump to the next set of pages (next 5 pages)
  const handleNextSet = () => {
    const nextPage = Math.min(currentPage + 5, totalPages);
    onPageChange(nextPage);
  };

  // Jump to the previous set of pages (previous 5 pages)
  const handlePreviousSet = () => {
    const prevPage = Math.max(currentPage - 5, 1);
    onPageChange(prevPage);
  };

  const onPageClick = (page: number | string) => {
    if (page === "previousJump") {
      handlePreviousSet();
    } else if (page === "nextJump") {
      handleNextSet();
    } else {
      onPageChange(page as number);
    }
  };

  const visiblePages = getVisiblePages(currentPage, totalPages);

  const currentPageStart = (currentPage - 1) * pageSize + 1;
  const currentPageEnd = Math.min(currentPage * pageSize, totalCount);

  const nextPageLabel = intl.formatMessage({
    id: "commonTextInstances.fyiNextStep",
    defaultMessage: "Next"
  });

  const prevPageLabel = intl.formatMessage({
    id: "common.prev",
    defaultMessage: "Prev"
  });

  const tableOverviewLabel = intl.formatMessage(
    {
      id: "common.tableOverview",
      defaultMessage:
        "Showing {currentPageStart}-{currentPageEnd} out of {totalCount} {totalCount, plural, one {item} other {items}}"
    },
    {
      currentPageStart,
      currentPageEnd,
      totalCount
    }
  );

  return (
    <Stack
      direction="horizontal"
      align="center"
      justify="space-between"
      padding="8 16"
      width="100%"
    >
      <Typography
        variant={TypographyVariants.LABEL_SM}
        color={CatalystColors.NEUTRAL_700}
      >
        {tableOverviewLabel}
      </Typography>
      <Stack direction="horizontal" align="center" gap="4">
        <button
          className={classNames({
            [styles.tablePaginationButton]: true,
            [styles.tablePaginationPrevButton]: true
          })}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          type="button"
        >
          <ChevronLeft size={12} />
          {prevPageLabel}
        </button>
        {visiblePages.map(page => (
          <button
            className={classNames({
              [styles.tablePaginationButton]: true,
              [styles.tablePaginationButtonActive]: currentPage === page
            })}
            key={page}
            onClick={() => onPageClick(page)}
            type="button"
          >
            {page === "previousJump" || page === "nextJump" ? "..." : page}
          </button>
        ))}
        <button
          className={classNames({
            [styles.tablePaginationButton]: true,
            [styles.tablePaginationNextButton]: true
          })}
          onClick={handleNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          type="button"
        >
          {nextPageLabel}
          <ChevronRight size={12} />
        </button>
      </Stack>
    </Stack>
  );
};
